import {
  Box,
  Divider,
  Flex,
  Icon,
  Spacer,
  Spinner,
  Table,
  Tbody,
  Td,
  Text as ChakraText,
  Th,
  Thead,
  Wrap,
  Tr,
} from '@chakra-ui/react'
import moment from 'moment'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'
import type { Column } from 'react-table'
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'
import { colors } from '../../../ui-kit'
import type { PurgeCacheResponse, UserSessionResponse } from '../../interfaces'
import Pagination from '../Pagination'
import SearchBar from '../SearchBar'

interface Props {
  user: UserSessionResponse | null
  data: PurgeCacheResponse[] | undefined
  loading: boolean
  getData(): Promise<void>
}

const PurgeCacheTable = ({
  user,
  data,
  loading,
  getData,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  useEffect(() => {
    void getData()
  }, [user])

  const tableData = useMemo(() => data ?? [], [data])

  const columns = useMemo<Array<Column<PurgeCacheResponse>>>(
    () => [
      {
        Header: t('url', 'URL').toString(),
        accessor: values => values.url,
        Cell: ({ value }: { value: string }) => (
          <Box maxW="50vw">
            <Wrap>
              <ChakraText>{value}</ChakraText>
            </Wrap>
          </Box>
        ),
      },
      {
        Header: t('time_of_submission', 'Time of Submission').toString(),
        accessor: values =>
          moment(values.createdAt).format('DD MMMM YYYY, hh:mm A'),
      },
      {
        Header: t('status', 'Status').toString(),
        accessor: values => {
          if (
            values.doNotInvalidateCDNCache === false &&
            values.isPurgeComplete &&
            values.invalidatedCDN
          ) {
            return t('complete', 'Complete').toString()
          } else if (
            values.doNotInvalidateCDNCache === true &&
            values.isPurgeComplete
          ) {
            return t('complete', 'Complete').toString()
          } else if(values.error) {
            return values.error;
          }
          return t('in_progress', 'In Progress').toString()
        },
      },
    ],
    [],
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    rows,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageCount,
    setPageSize,
    prepareRow,
    state: { globalFilter, pageIndex, pageSize },
    setGlobalFilter,
    gotoPage,
    pageOptions,
  } = useTable<PurgeCacheResponse>(
    { columns, data: tableData },
    useGlobalFilter,
    useSortBy,
    usePagination,
  )
  const pageOffset = pageIndex * pageSize

  return (
    <React.Fragment>
      <Flex align="center">
        <SearchBar
          searchQuery={globalFilter}
          setSearchQuery={setGlobalFilter}
          padding="1.5em 0em"
          minWidth="30em"
        />
        <Spacer />
        {rows.length ? (
          <ChakraText fontWeight="500">
            {t('showing', 'Showing')} {pageOffset + (pageCount === 0 ? 0 : 1)}{' '}
            {t('to', 'to')}{' '}
            {pageOffset + pageSize <= rows.length
              ? pageOffset + pageSize
              : rows.length}{' '}
            {t('of', 'of')} {rows.length} {t('entries', 'entries')}
          </ChakraText>
        ) : null}
      </Flex>
      <Table {...getTableProps}>
        <Thead bgColor="appPaleGreyTwo">
          {headerGroups.map(headerGroup => {
            const { key, ...restHeaderGroupProps } =
              headerGroup.getHeaderGroupProps()
            return (
              <Tr key={key} {...restHeaderGroupProps}>
                {headerGroup.headers.map(column => {
                  const { key: keyCol, ...restColumn } = column.getHeaderProps(
                    column.getSortByToggleProps(),
                  )
                  return (
                    <Th key={keyCol} {...restColumn}>
                      {column.render('Header')}
                      {!column.disableSortBy ? (
                        <Box as="span" pl="2" color={colors.appBattleshipGrey}>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon
                                as={FaSortUp}
                                aria-label={t(
                                  'sorted_descending',
                                  'sorted descending',
                                )}
                              />
                            ) : (
                              <Icon
                                as={FaSortDown}
                                aria-label={t(
                                  'sorted_ascending',
                                  'sorted ascending',
                                )}
                              />
                            )
                          ) : (
                            <Icon
                              as={FaSort}
                              aria-label={t('sort_by_column', 'sort by column')}
                            />
                          )}
                        </Box>
                      ) : null}
                    </Th>
                  )
                })}
              </Tr>
            )
          })}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {!loading ? (
            rows.length ? (
              page.map(row => {
                prepareRow(row)
                const { key, ...restRowProps } = row.getRowProps()
                return (
                  <Tr key={key} {...restRowProps}>
                    {row.cells.map(cell => {
                      const { key: keyRow, ...restCellProps } =
                        cell.getCellProps()
                      return (
                        <Td key={keyRow} {...restCellProps}>
                          <ChakraText>{cell.render('Cell')}</ChakraText>
                        </Td>
                      )
                    })}
                  </Tr>
                )
              })
            ) : (
              <Tr>
                <Td colSpan={3} textAlign="center">
                  <ChakraText>
                    {t(
                      'no_purge_request_so_far',
                      'No purge request submitted so far',
                    )}
                  </ChakraText>
                </Td>
              </Tr>
            )
          ) : (
            <Tr>
              <Td colSpan={3} textAlign="center">
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="appPaleGrey"
                  color="appBlue"
                  size="xl"
                />
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
      {pageOptions.length > 0 ? <Divider margin="3em 0em 1em" /> : null}
      <Flex>
        <Spacer />
        <Pagination
          pageIndex={pageIndex}
          pageSize={pageSize}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          previousPage={previousPage}
          nextPage={nextPage}
          gotoPage={gotoPage}
        />
      </Flex>
    </React.Fragment>
  )
}

export default PurgeCacheTable
